import React, { useState, useEffect } from 'react';
import { 
    Layout, 
    Card, 
    Row, 
    Col, 
    Table, 
    Button, 
    Space, 
    Select, 
    DatePicker, 
    message, 
    Typography 
} from 'antd';
import { 
    SearchOutlined, 
    ClearOutlined, 
    FileExcelOutlined, 
    ArrowLeftOutlined,
    BarChartOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import Headerdiv from "../layout/header";
import Footerdiv from "../layout/footer";
import axiosClient from '../../api/axiosClient';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title as ChartTitle,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ChartTitle,
  Tooltip,
  Legend
);

const { Content } = Layout;

const SalesMonthlySummary = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [salesItems, setSalesItems] = useState([]);
    const [filters, setFilters] = useState({
        selectedMonth: dayjs(),
        sp_id: ''
    });

    // Fetch Sales Items for filter
    useEffect(() => {
        const fetchSalesItems = async () => {
            try {
                const response = await axiosClient.get('/sales/get_all_sales_items_dropdown.php');
                if (response.data.status === 'success') {
                    setSalesItems(response.data.data);
                }
            } catch (error) {
                console.error('Fetch sales items error:', error);
            }
        };
        fetchSalesItems();
    }, []);

    const fetchReport = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.append('selected_date', filters.selectedMonth.format('YYYY-MM-DD'));
            if (filters.sp_id) params.append('sp_id', filters.sp_id);

            const response = await axiosClient.get(`/reports/get_sales_monthly_summary.php?${params.toString()}`);
            if (response.data.status === 'success') {
                setData(response.data.data || []);
            }
        } catch (error) {
            console.error('Fetch report error:', error);
            message.error('Failed to fetch sales summary.');
        } finally {
            setLoading(false);
        }
    };


    const handleClear = () => {
        setFilters({
            selectedMonth: dayjs(),
            sp_id: ''
        });
        // fetchReport will be triggered manually by user or I can re-fetch
    };

    const exportToExcel = () => {
        if (data.length === 0) return;
        
        const worksheetData = data.map(item => ({
            'Month': item.month,
            'Sale': item.total
        }));

        const ws = XLSX.utils.json_to_sheet(worksheetData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Sales Summary");
        const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const finalData = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });
        saveAs(finalData, `Sales_Monthly_Summary_${dayjs().format('YYYYMMDD')}.xlsx`);
    };

    const chartData = {
        labels: data.map(item => dayjs(item.month).format('MMM YYYY')),
        datasets: [
            {
                label: 'Monthly Sales (Rs.)',
                data: data.map(item => item.total),
                backgroundColor: 'rgba(24, 144, 255, 0.7)',
                borderColor: 'rgba(24, 144, 255, 1)',
                borderWidth: 1,
            },
        ],
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
            },
            title: {
                display: true,
                text: '12-Month Sales Trend',
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    callback: (value) => `Rs. ${value.toLocaleString()}`
                }
            }
        }
    };

    const columns = [
        {
            title: 'Month',
            dataIndex: 'month',
            key: 'month',
            render: (text) => dayjs(text).format('MMMM YYYY'),
        },
        {
            title: 'Sale',
            dataIndex: 'total',
            key: 'total',
            align: 'right',
            render: (total) => `Rs. ${Number(total).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        }
    ];

    return (
        <Layout style={{ minHeight: '100vh', backgroundColor: '#f0f2f5' }}>
            <Headerdiv />
            <Content style={{ padding: '24px' }}>
                <div style={{ marginBottom: 16 }}>
                    <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/reports')}>Back to Reports</Button>
                </div>

                {/* Filter and Graph Card */}
                <Card 
                    title={
                        <Space>
                            <BarChartOutlined />
                            <span>Sales Monthly Summary</span>
                        </Space>
                    }
                    extra={
                        <Space wrap>
                            <Button 
                                type="primary" 
                                icon={<SearchOutlined />} 
                                onClick={fetchReport} 
                                loading={loading}
                            >
                                Generate
                            </Button>
                            <Button icon={<ClearOutlined />} onClick={handleClear}>Clear</Button>
                        </Space>
                    }
                    style={{ marginBottom: 24 }}
                >
                    <Row gutter={[24, 24]}>
                        <Col xs={24} md={8}>
                            <Row gutter={[0, 16]}>
                                <Col span={24}>
                                    <div style={{ marginBottom: 4, fontWeight: 500 }}>Select Month (End of Period)</div>
                                    <DatePicker 
                                        picker="month" 
                                        style={{ width: '100%' }} 
                                        value={filters.selectedMonth}
                                        onChange={(val) => setFilters({...filters, selectedMonth: val})}
                                        allowClear={false}
                                    />
                                </Col>
                                <Col span={24}>
                                    <div style={{ marginBottom: 4, fontWeight: 500 }}>Sale Item Wise Filter</div>
                                    <Select 
                                        style={{ width: '100%' }} 
                                        placeholder="All Sales Items"
                                        value={filters.sp_id}
                                        onChange={(val) => setFilters({...filters, sp_id: val})}
                                        allowClear
                                    >
                                        <Select.Option value="">All Items</Select.Option>
                                        {salesItems.map(item => (
                                            <Select.Option key={item.sp_id} value={item.sp_id}>
                                                {item.sp_code} - {item.sp_title}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Col>
                            </Row>
                        </Col>
                        <Col xs={24} md={16}>
                            <div style={{ height: '300px' }}>
                                <Bar data={chartData} options={chartOptions} />
                            </div>
                        </Col>
                    </Row>
                </Card>

                {/* Results Card */}
                <Card 
                    title="Monthly Data"
                    extra={
                        data.length > 0 && (
                            <Button 
                                icon={<FileExcelOutlined />} 
                                onClick={exportToExcel}
                                style={{ backgroundColor: '#217346', color: '#fff', borderColor: '#217346' }}
                            >
                                Export to Excel
                            </Button>
                        )
                    }
                >
                    <Table 
                        dataSource={data}
                        columns={columns}
                        rowKey="month"
                        loading={loading}
                        pagination={false}
                        bordered
                        size="small"
                        footer={() => (
                           <div style={{ textAlign: 'right', fontWeight: 'bold' }}>
                               Total Sales: Rs. {data.reduce((sum, item) => sum + item.total, 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                           </div>
                        )}
                    />
                </Card>
            </Content>
            <Footerdiv />
        </Layout>
    );
};

export default SalesMonthlySummary;
